import { useMemo, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    FormControlLabel,
    IconButton,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { useAnalysisChartConquestModeQuery } from 'src/context/api/non-branded/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import ConquestModeAnalysisChart from './conquest-mode-analysis-chart';
import ChartExportModal, {
    EXPORT_HEIGHT,
    EXPORT_WIDTH,
    type ExportFormat,
    type ExportOptions,
} from 'src/components/chart/chart-export-modal';
import InfiniteScrollKeywords from '../../../InfiniteScrollKeywords';
import { useSelector } from 'react-redux';

type ConquestModeAnalysisChartModalProps = {
    open: boolean;
    onClose: () => void;
};

type MetricOption = {
    key: string;
    label: string;
};

type GranularityOption = {
    value: 'weekly' | 'monthly';
    label: string;
};

type PeriodOption = {
    value: string;
    label: string;
};

const MAX_METRICS = 2;

const EXPORT_COLORS: Record<string, string> = {
    competitor_visibility_rate: '#9BA5AE',
    top_paid_presence_rate: '#DAA335',
    abs_top_paid_presence_rate: '#DAA335',
    clicks: '#DAA335',
    cost: '#DAA335',
    avg_cpc: '#DAA335',
    conversions: '#0052B4',
    revenue: '#0052B4',
};

const METRICS: MetricOption[] = [
    {
        key: 'competitor_visibility_rate',
        label: 'Competitor visibility rate',
    },
    {
        key: 'top_paid_presence_rate',
        label: 'Paid Top %',
    },
    {
        key: 'abs_top_paid_presence_rate',
        label: 'Paid First %',
    },
    {
        key: 'clicks',
        label: 'Paid Clicks',
    },
    {
        key: 'cost',
        label: 'Ad cost',
    },
    {
        key: 'avg_cpc',
        label: 'Paid CPC',
    },
    {
        key: 'conversions',
        label: 'Paid Conversions',
    },
    {
        key: 'revenue',
        label: 'Paid Revenue',
    },
];

const GRANULARITY_OPTIONS: GranularityOption[] = [
    {
        value: 'weekly',
        label: 'Weekly',
    },
    {
        value: 'monthly',
        label: 'Monthly',
    },
];

const WEEKLY_PERIOD_OPTIONS: PeriodOption[] = [
    {
        value: 'last_week',
        label: 'Last week',
    },
    {
        value: 'last_2_weeks',
        label: 'Last 2 weeks',
    },
    {
        value: 'last_3_weeks',
        label: 'Last 3 weeks',
    },
    {
        value: 'last_4_weeks',
        label: 'Last 4 weeks',
    },
    {
        value: 'last_5_weeks',
        label: 'Last 5 weeks',
    },
    {
        value: 'last_6_weeks',
        label: 'Last 6 weeks',
    },
    {
        value: 'last_7_weeks',
        label: 'Last 7 weeks',
    },
    {
        value: 'last_8_weeks',
        label: 'Last 8 weeks',
    },
    {
        value: 'last_9_weeks',
        label: 'Last 9 weeks',
    },
    {
        value: 'last_10_weeks',
        label: 'Last 10 weeks',
    },
    {
        value: 'last_11_weeks',
        label: 'Last 11 weeks',
    },
    {
        value: 'last_12_weeks',
        label: 'Last 12 weeks',
    },
];

const MONTHLY_PERIOD_OPTIONS: PeriodOption[] = [
    {
        value: 'last_month',
        label: 'Last month',
    },
    {
        value: 'last_2_months',
        label: 'Last 2 months',
    },
    {
        value: 'last_3_months',
        label: 'Last 3 months',
    },
    {
        value: 'last_4_months',
        label: 'Last 4 months',
    },
    {
        value: 'last_5_months',
        label: 'Last 5 months',
    },
    {
        value: 'last_6_months',
        label: 'Last 6 months',
    },
    {
        value: 'last_7_months',
        label: 'Last 7 months',
    },
    {
        value: 'last_8_months',
        label: 'Last 8 months',
    },
    {
        value: 'last_9_months',
        label: 'Last 9 months',
    },
    {
        value: 'last_10_months',
        label: 'Last 10 months',
    },
    {
        value: 'last_11_months',
        label: 'Last 11 months',
    },
    {
        value: 'last_12_months',
        label: 'Last 12 months',
    },
];

export default function ConquestModeAnalysisChartModal({
    open,
    onClose,
}: ConquestModeAnalysisChartModalProps) {
    const [granularity, setGranularity]: any = useState<GranularityOption>(
        GRANULARITY_OPTIONS[0]
    );

    const [period, setPeriod]: any = useState<PeriodOption>(
        WEEKLY_PERIOD_OPTIONS[1]
    );

    const [metricSlots, setMetricSlots] = useState<string[]>([
        'competitor_visibility_rate',
        'cost',
    ]);

    const [hiddenMetrics, setHiddenMetrics] = useState<string[]>([]);
    const [previousPeriod, setPreviousPeriod] = useState(false);
    const [yearOverYear, setYearOverYear] = useState(false);
    const [showLabels, setShowLabels] = useState(false);
    const [matchScale, setMatchScale] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const chartExportRef = useRef<HTMLDivElement>(null);
    const apexChartInstanceRef = useRef<any>(null);
    const filterStore = useSelector((state: any) => state.filters);
    const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);

    const workspace: any = useCurrentWorkspace();

    const periodOptions = useMemo(
        () =>
            granularity.value === 'monthly'
                ? MONTHLY_PERIOD_OPTIONS
                : WEEKLY_PERIOD_OPTIONS,
        [granularity.value]
    );

    const selectedMetrics = useMemo(
        () => metricSlots.filter(Boolean),
        [metricSlots]
    );

    const visibleSelectedMetrics = useMemo(
        () =>
            selectedMetrics.filter(
                (metricKey) => !hiddenMetrics.includes(metricKey)
            ),
        [selectedMetrics, hiddenMetrics]
    );

    const handleGranularityChange = (
        value: GranularityOption | null
    ) => {
        if (!value) return;

        setGranularity(value);

        if (value.value === 'monthly') {
            setPeriod(MONTHLY_PERIOD_OPTIONS[1]);
        } else {
            setPeriod(WEEKLY_PERIOD_OPTIONS[1]);
        }
    };

    const handleMetricChange = (
        index: number,
        metricKey: string
    ) => {
        const previousMetric = metricSlots[index];

        if (!metricKey && selectedMetrics.length <= 1) {
            return;
        }

        if (previousMetric && previousMetric !== metricKey) {
            setHiddenMetrics((current) =>
                current.filter((key) => key !== previousMetric)
            );
        }

        setMetricSlots((current) => {
            const next = [...current];

            next[index] = metricKey;

            if (!metricKey) {
                const remaining = next.filter(Boolean);

                if (remaining.length >= MAX_METRICS) {
                    return remaining.slice(0, MAX_METRICS);
                }

                return [...remaining, ''];
            }

            const isLastSlot = index === next.length - 1;
            const canAddAnotherSlot = next.length < MAX_METRICS;

            if (isLastSlot && canAddAnotherSlot) {
                next.push('');
            }

            return next;
        });
    };

    const toggleMetricVisibility = (metricKey: string) => {
        if (!metricKey) return;

        const isHidden = hiddenMetrics.includes(metricKey);

        if (isHidden) {
            setHiddenMetrics((current) =>
                current.filter((key) => key !== metricKey)
            );

            return;
        }

        if (visibleSelectedMetrics.length <= 1) {
            return;
        }

        setHiddenMetrics((current) => [
            ...current,
            metricKey,
        ]);
    };

    const renderMetricSelect = (
        metricKey: string,
        index: number
    ) => {
        const label = `Metric ${index + 1}`;

        const selectedMetric: any =
            METRICS.find(
                (metric) => metric.key === metricKey
            ) ?? null;

        const availableMetrics = METRICS.filter(
            (metric) =>
                metric.key === metricKey ||
                !selectedMetrics.includes(metric.key)
        );

        const isHidden =
            Boolean(metricKey) &&
            hiddenMetrics.includes(metricKey);

        const cannotHideMetric =
            Boolean(metricKey) &&
            !isHidden &&
            visibleSelectedMetrics.length <= 1;

        return (
            <Box
                key={`metric-slot-${index}`}
                sx={{
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        pt: '8px',
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: '12px',
                            zIndex: 1,
                            px: '4px',
                            backgroundColor: '#FFFFFF',
                            color: '#91A0AF',
                            fontFamily: 'Public Sans',
                            fontSize: '12px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '12px',
                        }}
                    >
                        {label}
                    </Typography>

                    <Autocomplete
                        fullWidth
                        options={availableMetrics}
                        value={selectedMetric}
                        onChange={(_, newValue) => {
                            handleMetricChange(
                                index,
                                newValue?.key ?? ''
                            );
                        }}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, currentValue) =>
                            option.key === currentValue.key
                        }
                        popupIcon={
                            <Iconify
                                icon="eva:chevron-down-fill"
                                width={20}
                                sx={{
                                    color: '#91A0AF',
                                }}
                            />
                        }
                        slotProps={{
                            paper: {
                                sx: {
                                    mt: '6px',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    boxShadow:
                                        '0px 4px 12px rgba(145, 158, 171, 0.24)',
                                },
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: '46px',
                                minHeight: '46px',
                                pr: '70px !important',
                            },
                            '& .MuiAutocomplete-input': {
                                fontFamily: 'Public Sans',
                                fontSize: '16px',
                                fontWeight: 400,
                                color: isHidden
                                    ? '#919EAB'
                                    : '#333C47',
                            },
                            '& .MuiAutocomplete-endAdornment': {
                                right: '12px',
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select metric"
                            />
                        )}
                        disableClearable
                        renderOption={(props, option) => (
                            <Box
                                component="li"
                                {...props}
                                key={option.key}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Public Sans',
                                        fontSize: '14px',
                                        color: '#333C47',
                                    }}
                                >
                                    {option.label}
                                </Typography>
                            </Box>
                        )}
                    />

                    <IconButton
                        size="small"
                        disabled={!metricKey || cannotHideMetric}
                        onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            toggleMetricVisibility(metricKey);
                        }}
                        sx={{
                            position: 'absolute',
                            right: '38px',
                            top: 'calc(50% + 4px)',
                            transform: 'translateY(-50%)',
                            zIndex: 3,
                            width: 32,
                            height: 32,
                            p: 0,
                            color: isHidden
                                ? '#C4CDD5'
                                : '#91A0AF',
                            '&:hover': {
                                backgroundColor:
                                    'rgba(145, 158, 171, 0.08)',
                            },
                            '&.Mui-disabled': {
                                color: '#C4CDD5',
                            },
                        }}
                    >
                        <Iconify
                            icon={
                                isHidden
                                    ? 'solar:eye-closed-linear'
                                    : 'solar:eye-linear'
                            }
                            width={22}
                        />
                    </IconButton>
                </Box>
            </Box>
        );
    };

    const selectedComparison: 'none' | 'pp' | 'ly' = previousPeriod
        ? 'pp'
        : yearOverYear
            ? 'ly'
            : 'none';

    const {
        data: analysisChartData,
        isLoading: isAnalysisChartLoading,
        isFetching: isAnalysisChartFetching,
    }: any = useAnalysisChartConquestModeQuery(
        {
            workspace_id: workspace?.id,
            client_id: workspace?.bqTable ?? '',
            region_name: [],
            period: period.value,
            comparison: selectedComparison,
            metrics: selectedMetrics,
            keywords: selectedKeywords,
            granularity: granularity.value,
        },
        {
            skip:
                !open ||
                !workspace?.id ||
                !workspace?.bqTable ||
                selectedMetrics.length === 0,
        }
    );

    const handleExport = async (_format: ExportFormat, exportOptions: ExportOptions) => {
        const element = chartExportRef.current;
        const instance = apexChartInstanceRef.current;
        if (!element || !instance || typeof instance.dataURI !== 'function' || exporting) return;

        setExporting(true);
        try {
            await document.fonts.ready;
            const chartElement = (instance?.el ?? element.querySelector('.apexcharts-canvas')) as HTMLElement;
            chartElement.querySelectorAll<SVGPathElement>('.conquest-incomplete-segment').forEach((segment) => {
                segment.getAnimations().forEach((animation) => {
                    try { animation.finish(); } catch { /* Animation may already be idle. */ }
                });
                segment.style.clipPath = 'none';
            });
            // Always use ApexCharts' native image generator: SVG Blob URLs may not decode
            // when the chart contains fonts, annotations or CSS from the application.
            const { imgURI } = await instance.dataURI({ scale: 2 });
            if (!imgURI) throw new Error('ApexCharts did not return an image');
            const sourceImage = new Image();
            sourceImage.src = imgURI;
            await sourceImage.decode();

            const canvas = document.createElement('canvas');
            canvas.width = EXPORT_WIDTH;
            canvas.height = EXPORT_HEIGHT;
            const context = canvas.getContext('2d');
            if (!context) throw new Error('Canvas is not available');

            if (!exportOptions.transparent) {
                context.fillStyle = '#FFFFFF';
                context.fillRect(0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);
            }
            const comparisonColumns = element.querySelectorAll<HTMLElement>('[data-export-comparison-index]');
            const chartRect = chartElement.getBoundingClientRect();
            const hasComparison = comparisonColumns.length > 0;
            const padding = 48;
            const chartTop = 150;
            const chartBottom = hasComparison ? 620 : 752;
            // 'No axes' exports only the plotting area, excluding the surrounding labels.
            const plotRect = chartElement.querySelector<SVGGraphicsElement>('.apexcharts-grid');
            const gridBounds = plotRect?.getBoundingClientRect();
            const hasPlotBounds = exportOptions.hideAxes && gridBounds && gridBounds.width > 0 && gridBounds.height > 0 && chartRect.width > 0;
            const sourceX = hasPlotBounds ? Math.max(0, (gridBounds.left - chartRect.left) / chartRect.width * sourceImage.width) : 0;
            const sourceY = hasPlotBounds ? Math.max(0, (gridBounds.top - chartRect.top) / chartRect.height * sourceImage.height) : 0;
            const sourceWidth = hasPlotBounds ? Math.min(sourceImage.width - sourceX, gridBounds.width / chartRect.width * sourceImage.width) : sourceImage.width;
            const sourceHeight = hasPlotBounds ? Math.min(sourceImage.height - sourceY, gridBounds.height / chartRect.height * sourceImage.height) : sourceImage.height;
            const scale = Math.min(
                (EXPORT_WIDTH - padding * 2) / sourceWidth,
                (chartBottom - chartTop) / sourceHeight
            );
            const width = sourceWidth * scale;
            const height = sourceHeight * scale;
            const chartX = (EXPORT_WIDTH - width) / 2;
            context.drawImage(sourceImage, sourceX, sourceY, sourceWidth, sourceHeight, chartX, chartTop, width, height);

            // The MUI legend sits outside the ApexCharts SVG, so draw it separately.
            context.font = '500 21px Public Sans, sans-serif';
            context.textBaseline = 'middle';
            let legendX = EXPORT_WIDTH - padding;
            [...visibleSelectedMetrics].reverse().forEach((key) => {
                const label = METRICS.find((metric) => metric.key === key)?.label ?? key;
                const textWidth = context.measureText(label).width;
                legendX -= textWidth;
                context.textAlign = 'left';
                context.fillStyle = '#161C24';
                context.fillText(label, legendX, 95);
                legendX -= 16;
                context.beginPath();
                context.arc(legendX, 95, 5, 0, Math.PI * 2);
                context.fillStyle = EXPORT_COLORS[key] ?? '#919EAB';
                context.fill();
                legendX -= 28;
            });

            // Comparison values are HTML below the SVG; redraw them aligned with X-axis labels.
            if (hasComparison && chartRect.width) {
                context.font = '18px Public Sans, sans-serif';
                const periodLabel = element.querySelector<HTMLElement>('[data-export-comparison-period]');
                if (periodLabel) {
                    context.fillStyle = '#919EAB';
                    context.textAlign = 'left';
                    periodLabel.textContent?.split('\n').forEach((line, index) => {
                        context.fillText(line, chartX + 8, chartTop + height + 37 + index * 19);
                    });
                }
                comparisonColumns.forEach((column) => {
                    const columnRect = column.getBoundingClientRect();
                    const originalX = ((columnRect.left + columnRect.width / 2 - chartRect.left) / chartRect.width) * sourceImage.width;
                    const x = chartX + ((originalX - sourceX) / sourceWidth) * width;
                    const rows = column.querySelectorAll<HTMLElement>('[data-export-comparison-row]');
                    rows.forEach((row, index) => {
                        const value = row.querySelector<HTMLElement>('.MuiTypography-root');
                        const text = value?.textContent ?? '—';
                        const y = chartTop + height + 37 + index * 23;
                        context.textAlign = 'center';
                        context.fillStyle = value ? getComputedStyle(value).color : '#919EAB';
                        context.fillText(text, x, y);
                    });
                });
            }

            if (exportOptions.transparent) {
                // The native Apex image may contain an opaque white background.
                // Remove its near-white pixels to obtain a PNG with an alpha channel.
                const pixels = context.getImageData(0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);
                for (let index = 0; index < pixels.data.length; index += 4) {
                    const red = pixels.data[index];
                    const green = pixels.data[index + 1];
                    const blue = pixels.data[index + 2];
                    if (red >= 248 && green >= 248 && blue >= 248) pixels.data[index + 3] = 0;
                }
                context.putImageData(pixels, 0, 0);
            }

            const link = document.createElement('a');
            link.download = `conquest-mode-chart-${exportOptions.transparent ? 'transparent' : 'background'}-${exportOptions.hideAxes ? 'no-axes' : 'with-axes'}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            setExportModalOpen(false);
        } catch (error) {
            console.error('Unable to export Conquest Mode chart:', error);
        } finally {
            setExporting(false);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: '1600px',
                        maxWidth: 'calc(100vw - 48px)',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        backgroundColor: '#FFFFFF',
                        boxShadow:
                            '0px 24px 48px rgba(145, 158, 171, 0.24)',
                    },
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(33, 43, 54, 0.48)',
                    },
                }}
            >
                <Box
                    sx={{
                        height: '60px',
                        minHeight: '60px',
                        px: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#FAFBFB',
                        borderBottom:
                            '1px solid rgba(145, 158, 171, 0.20)',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#333C47',
                                fontFamily: 'Public Sans',
                                fontSize: '18px',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                mr: '16px',
                                lineHeight: '100%',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Metric Evolution
                        </Typography>


                        <Box mr={1}>
                            <InfiniteScrollKeywords
                                keywords={filterStore?.loadedKeyword || []}
                                onKeywordChange={(keywords: any) => {
                                    setSelectedKeywords(keywords);
                                }}
                                disableReduxUpdate
                            />
                        </Box>

                        <Autocomplete
                            disableClearable
                            options={GRANULARITY_OPTIONS}
                            value={granularity}
                            onChange={(_, newValue) => {
                                if (!newValue) return;

                                handleGranularityChange(newValue);
                            }}
                            getOptionLabel={(option) =>
                                option.label
                            }
                            isOptionEqualToValue={(
                                option,
                                currentValue
                            ) =>
                                option.value ===
                                currentValue.value
                            }
                            popupIcon={
                                <Iconify
                                    icon="eva:chevron-down-fill"
                                    width={18}
                                    sx={{
                                        color: '#91A0AF',
                                    }}
                                />
                            }
                            sx={{
                                width: 120,
                                '& .MuiOutlinedInput-root': {
                                    height: 40,
                                    minHeight: 40,
                                    p: '8px 8px',
                                    borderRadius: '12px',
                                },
                                '& .MuiAutocomplete-input': {
                                    width: 0,
                                    minWidth: 0,
                                    opacity: 0,
                                    p: '0 !important',
                                },
                                '& .MuiAutocomplete-endAdornment': {
                                    right: 8,
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputProps={{
                                        ...params.inputProps,
                                        readOnly: true,
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <Box
                                                sx={{
                                                    display:
                                                        'flex',
                                                    alignItems:
                                                        'center',
                                                    justifyContent:
                                                        'center',
                                                    height: '28px',
                                                    px: '12px',
                                                    borderRadius:
                                                        '8px',
                                                    bgcolor:
                                                        '#F4F6F8',
                                                    color:
                                                        '#333C47',
                                                    fontFamily:
                                                        'Public Sans',
                                                    fontSize:
                                                        '13px',
                                                    fontStyle:
                                                        'normal',
                                                    fontWeight: 500,
                                                    lineHeight:
                                                        '23px',
                                                    whiteSpace:
                                                        'nowrap',
                                                }}
                                            >
                                                {
                                                    granularity.label
                                                }
                                            </Box>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <Autocomplete
                            disableClearable
                            options={periodOptions}
                            value={period}
                            onChange={(_, newValue) => {
                                if (!newValue) return;

                                setPeriod(newValue);
                            }}
                            getOptionLabel={(option) =>
                                option.label
                            }
                            isOptionEqualToValue={(
                                option,
                                currentValue
                            ) =>
                                option.value ===
                                currentValue.value
                            }
                            popupIcon={
                                <Iconify
                                    icon="eva:chevron-down-fill"
                                    width={18}
                                    sx={{
                                        color: '#91A0AF',
                                    }}
                                />
                            }
                            sx={{
                                width: 150,
                                '& .MuiOutlinedInput-root': {
                                    height: 40,
                                    minHeight: 40,
                                    p: '4px 8px',
                                    borderRadius: '12px',
                                },
                                '& .MuiAutocomplete-input': {
                                    width: 0,
                                    minWidth: 0,
                                    opacity: 0,
                                    p: '0 !important',
                                },
                                '& .MuiAutocomplete-endAdornment': {
                                    right: 8,
                                },
                            }}
                            slotProps={{
                                paper: {
                                    sx: {
                                        mt: '4px',
                                        borderRadius: '8px',
                                        boxShadow:
                                            '0px 4px 12px rgba(145, 158, 171, 0.24)',
                                        '& .MuiAutocomplete-listbox': {
                                            maxHeight:
                                                '260px',
                                        },
                                    },
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputProps={{
                                        ...params.inputProps,
                                        value: '',
                                        readOnly: true,
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <Box
                                                sx={{
                                                    display:
                                                        'flex',
                                                    alignItems:
                                                        'center',
                                                    justifyContent:
                                                        'center',
                                                    height: '28px',
                                                    px: '12px',
                                                    borderRadius:
                                                        '8px',
                                                    bgcolor:
                                                        '#F4F6F8',
                                                    color:
                                                        '#333C47',
                                                    fontFamily:
                                                        'Public Sans',
                                                    fontSize:
                                                        '13px',
                                                    fontStyle:
                                                        'normal',
                                                    fontWeight: 500,
                                                    lineHeight:
                                                        '23px',
                                                    whiteSpace:
                                                        'nowrap',
                                                }}
                                            >
                                                {period.label}
                                            </Box>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Box>

                    <IconButton
                        onClick={onClose}
                        size="small"
                        aria-label="Close metric evolution modal"
                        sx={{
                            color: '#637381',
                        }}
                    >
                        <Iconify
                            icon="mingcute:close-line"
                            width={20}
                        />
                    </IconButton>
                </Box>

                <DialogContent
                    sx={{
                        p: '24px 20px 32px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                md: 'repeat(3, minmax(0, 1fr))',
                            },
                            gap: '24px',
                        }}
                    >
                        {metricSlots.map((metric, index) =>
                            renderMetricSelect(
                                metric,
                                index
                            )
                        )}
                    </Box>


                    <Box
                        ref={chartExportRef}
                        sx={{
                            mt: '24px',
                            width: '100%',
                            minWidth: 0,
                        }}
                    >
                        {isAnalysisChartLoading || isAnalysisChartFetching ? (
                            <Skeleton
                                variant="rectangular"
                                animation="wave"
                                width="100%"
                                height={320}
                                sx={{ borderRadius: '8px' }}
                            />
                        ) : (
                            <ConquestModeAnalysisChart
                                data={analysisChartData}
                                metrics={selectedMetrics}
                                hiddenMetrics={hiddenMetrics}
                                comparison={selectedComparison}
                                granularity={granularity.value}
                                showLabels={showLabels}
                                matchScale={matchScale}
                                onChartMounted={(instance) => { apexChartInstanceRef.current = instance; }}
                            />
                        )}
                    </Box>

                </DialogContent>

                <Box
                    sx={{
                        minHeight: '54px',
                        px: '20px',
                        py: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderTop:
                            '1px solid rgba(145, 158, 171, 0.20)',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={previousPeriod}
                                    onChange={(event) => {
                                        const checked =
                                            event.target.checked;

                                        setPreviousPeriod(
                                            checked
                                        );

                                        if (checked) {
                                            setYearOverYear(
                                                false
                                            );
                                        }
                                    }}
                                />
                            }
                            label="Previous Period"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={yearOverYear}
                                    onChange={(event) => {
                                        const checked =
                                            event.target.checked;

                                        setYearOverYear(
                                            checked
                                        );

                                        if (checked) {
                                            setPreviousPeriod(
                                                false
                                            );
                                        }
                                    }}
                                />
                            }
                            label="Year over Year"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={showLabels}
                                    onChange={(event) =>
                                        setShowLabels(
                                            event.target.checked
                                        )
                                    }
                                />
                            }
                            label="Show labels"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={matchScale}
                                    onChange={(event) =>
                                        setMatchScale(
                                            event.target.checked
                                        )
                                    }
                                />
                            }
                            label="Match scale"
                        />
                    </Box>

                    <Button
                        variant="contained"
                        onClick={() => setExportModalOpen(true)}
                        disabled={isAnalysisChartLoading || isAnalysisChartFetching || visibleSelectedMetrics.length === 0}
                        sx={{
                            height: '30px',
                            px: '12px',
                            borderRadius: '6px',
                            backgroundColor: '#161C24',
                            color: '#FFFFFF',
                            boxShadow: 'none',
                            textTransform: 'none',
                            fontFamily: 'Public Sans',
                            fontSize: '12px',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: '#333C47',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Export chart
                    </Button>
                </Box>
            </Dialog>

            <ChartExportModal
                open={exportModalOpen && open}
                onClose={() => setExportModalOpen(false)}
                onExport={handleExport}
                exporting={exporting}
            />
        </>
    );
}